/*!
 * shapeForms.js — a shape's outline as its distance from the middle, all round
 * © 2026 Rob Canning — GPLv3
 *
 *     shape(m:5, n1:0.3, n2:0.3, n3:0.3)     a five-pointed star
 *     shape(m:6, n1:1, n2:7, n3:8)            a flower
 *     shape(m:3, n1:4.5, n2:10, n3:10)        a soft triangle
 *     shape(m:follow(bass.low, 2, 8))         the form with the sound
 *     shape(uid:a, sides:4, morph:2)          ... into a square over 2 beats
 *
 * The superformula (Johan Gielis): from four numbers, stars, flowers,
 * blobs, polygons with soft or pinched sides --
 *
 *     r(φ) = ( |cos(mφ/4)|^n2 + |sin(mφ/4)|^n3 ) ^ (-1/n1)
 *
 * m is how many times it repeats round, n1 how round (small: pinched to
 * points, large: swollen), n2 and n3 the shape of each side.
 *
 * Every outline -- a superformula, a polygon, a circle -- can be said as
 * radii at the same angles round the middle, the first straight up. That
 * makes a morph between any two of them a mix of their radii, angle by
 * angle: always closed, never a jump. An m between two whole numbers is
 * the mix of the two forms (a fractional m alone leaves a seam).
 */

/** Points round an outline: enough for m:24 to keep its points sharp. */
export const RADII = 720;
/** This many sides or more draws a circle. */
export const CIRCLE_FROM = 40;

/** A superformula form, from what was written: m, n1, n2, n3 (each 1 when not given). */
export function formOf(src) {
  const v = k => (Number.isFinite(Number(src?.[k])) ? Number(src[k]) : 1);
  return { m: Math.max(0, v("m")), n1: v("n1") || 1e-3, n2: v("n2"), n3: v("n3") };
}

/** Radii of one superformula with a whole m, normalised to a largest of 1. */
function wholeForm(m, n1, n2, n3, N) {
  const r = new Float32Array(N);
  let max = 0;
  for (let k = 0; k < N; k++) {
    // from straight up, so a point (a lobe) is at the top
    const phi = (2 * Math.PI * k) / N;
    const t = (m * phi) / 4;
    const v = Math.abs(Math.cos(t)) ** n2 + Math.abs(Math.sin(t)) ** n3;
    const rr = v > 0 ? v ** (-1 / n1) : 0;
    r[k] = Number.isFinite(rr) ? rr : 0;
    if (r[k] > max) max = r[k];
  }
  if (max > 0) for (let k = 0; k < N; k++) r[k] /= max;
  return r;
}

/** A form's radii: a fractional m is the mix of the whole ones either side. */
export function formRadii(form, N = RADII) {
  const { m, n1, n2, n3 } = form;
  const lo = Math.floor(m), f = m - lo;
  const a = wholeForm(lo, n1, n2, n3, N);
  if (f < 1e-6) return a;
  return mixRadii(a, wholeForm(lo + 1, n1, n2, n3, N), f);
}

/**
 * A regular polygon's radii (a circle's, 0 or CIRCLE_FROM+ sides), as shape.js
 * draws it: a point at the top for odd counts, a flat top for even ones.
 */
export function polygonRadii(sides, N = RADII) {
  const r = new Float32Array(N);
  if (!(sides >= 3) || sides >= CIRCLE_FROM) return r.fill(1);
  const seg = (2 * Math.PI) / sides;
  const off = sides % 2 === 0 ? seg / 2 : 0;           // a vertex's angle from straight up
  for (let k = 0; k < N; k++) {
    const phi = (2 * Math.PI * k) / N;
    let d = ((phi - off) % seg + seg) % seg - seg / 2;   // from the middle of the side
    r[k] = Math.cos(seg / 2) / Math.cos(d);
  }
  return r;
}

/** The mix of two outlines' radii: 0 is `a`, 1 is `b`. */
export function mixRadii(a, b, t) {
  const out = new Float32Array(a.length);
  for (let k = 0; k < a.length; k++) out[k] = a[k] + (b[k] - a[k]) * t;
  return out;
}

/**
 * The outline as path data round (cx, cy), radius rx across and ry down
 * at 1. Angle 0 is straight up, going clockwise (the screen's y is down).
 */
export function radiiPath(radii, cx, cy, rx, ry = rx) {
  const N = radii.length;
  let d = "";
  for (let k = 0; k < N; k++) {
    const phi = (2 * Math.PI * k) / N;
    const x = cx + Math.sin(phi) * radii[k] * rx, y = cy - Math.cos(phi) * radii[k] * ry;
    d += `${k ? "L" : "M"}${x.toFixed(2)} ${y.toFixed(2)}`;
  }
  return d + "Z";
}

/** A morph's ease: slow out, slow in. */
export const ease = t => (t < 0.5 ? 2 * t * t : 1 - (2 - 2 * t) ** 2 / 2);
